import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { getBranch } from "../../../utils/app.utils";

import ZoneApi, { IStallLookup, IZoneLookup } from "../../../api/zone/zone.api";
import PaymentTypeApi from "../../../api/setting/debttype/debttype.api";
import { PaymentListReportParams } from "../../../api/reports/reports.api";
import { PaymentTypeOption } from "./paymentList.types";
import { stallLabel } from "../../../utils/stall.utils";

/** ค่าของ dropdown ที่ยังไม่กรอง — sentinel ที่ไม่ใช่ '' เพราะ MUI Select ไม่เรียก renderValue ตอนค่าว่าง */
export const PAYMENT_LIST_FILTER_ALL = "ALL";

type FilterValue = number | typeof PAYMENT_LIST_FILTER_ALL;

/**
 * ค่าปรับไม่ใช่ "ประเภทบริการ" — backend ดึงบรรทัดนี้ไปรวมเป็นคอลัมน์ ค่าปรับ ของใบ
 * ไม่ได้โผล่ใน `รายละเอียด` เลือกจาก dropdown ไปจะได้ผลว่างเปล่า จึงซ่อนออกจากตัวเลือก
 */
const FINE_PAYMENT_SERVICE_ID = 5;

/**
 * แปลง response ของ GET zone/list ให้เหลือเฉพาะ field ที่ dropdown ใช้จริง
 *
 * เส้นนี้คืน array ดิบใน res.data แต่เผื่อแบบห่อ { data } ไว้ด้วยเพราะโมดูลใหม่ในโปรเจกต์
 * ห่อ envelope กันคนละแบบ · ตัดแผงที่ status = DELETED ออก (เส้นนี้ส่งแผงที่ถูกลบมาด้วย
 * ฝั่ง announce ก็กรองทิ้งเหมือนกัน ดู stallPicker.utils.ts:48-50)
 */
const toZoneLookup = (payload: any): IZoneLookup[] => {
  const rows: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return rows
    .map((zone: any) => ({
      zoneId: Number(zone?.zoneId),
      zoneName: zone?.zoneName ?? null,
      stall: (Array.isArray(zone?.stall) ? zone.stall : [])
        .filter((stall: any) => stall?.status !== "DELETED")
        .map((stall: any) => ({
          stallId: Number(stall?.stallId),
          stallName: stall?.stallName ?? null,
          stallNumber: stall?.stallNumber ?? null,
        }))
        .filter((stall: IStallLookup) => Number.isFinite(stall.stallId)),
    }))
    .filter((zone) => Number.isFinite(zone.zoneId));
};

/**
 * state ของแถบตัวกรองรายงานรายการชำระเงิน + ตัวเลือกโซน/แผงค้า/ประเภทค่าบริการ
 *
 * ต่างจาก useStallReclaimFilter ที่ยิง API ทุกครั้งที่ค่าตัวกรองเปลี่ยน (debounce 400ms)
 * หน้านี้ตามดีไซน์มีปุ่ม "ค้นหา" จึงต้องแยก 2 ชั้น:
 *   - draft  = ค่าที่ผู้ใช้กำลังกรอกอยู่ เปลี่ยนแล้ว "ไม่" ยิง API
 *   - applied = ค่าที่กดค้นหาแล้ว เป็นตัวเดียวที่ตาราง/pagination/Excel ใช้
 * ทำให้เปลี่ยนหน้าหรือกด Excel ได้ผลตรงกับที่เห็นบนตาราง ไม่ใช่ค่าที่พิมพ์ทิ้งไว้แต่ยังไม่กดค้นหา
 *
 * hook นี้ตั้งใจไม่ยิง API ของตาราง — คืนแค่ state กับ params ที่ประกอบแล้ว
 * ให้คอมโพเนนต์เขียน effect โหลดข้อมูลเอง
 */
export const usePaymentListFilter = () => {
  /**
   * สาขาที่เลือกไว้ตอน login — ต้องส่งทุกเส้น ไม่งั้นเห็นข้อมูลข้ามสาขา
   * (backend ไม่ได้ scope จาก token เพราะ user 1 คนไม่ผูกกับสาขาเดียว)
   */
  const branchId = useMemo(() => {
    const value = Number(getBranch());
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, []);

  /**
   * ช่วงวันที่เริ่มต้น = ทั้งปีปัจจุบัน ตามดีไซน์ (01/01 - 31/12)
   * แก้ค่านี้ต้องแก้ initializer ของ appliedParams ด้านล่างคู่กันเสมอ
   * ไม่งั้น fetch รอบแรกจะใช้ช่วงวันที่ไม่ตรงกับที่โชว์ในช่อง
   */
  const defaultRange = useMemo(
    () => ({
      start: moment().startOf("year").format("YYYY-MM-DD"),
      end: moment().endOf("year").format("YYYY-MM-DD"),
    }),
    []
  );

  /** ---- draft: ค่าที่กำลังกรอก ยังไม่มีผลกับตาราง ---- */
  const [keyword, setKeyword] = useState<string>("");
  const [zoneId, setZoneId] = useState<FilterValue>(PAYMENT_LIST_FILTER_ALL);
  const [stallId, setStallId] = useState<FilterValue>(PAYMENT_LIST_FILTER_ALL);
  const [paymentServiceId, setPaymentServiceId] = useState<FilterValue>(PAYMENT_LIST_FILTER_ALL);
  const [dateStart, setDateStart] = useState<string>(defaultRange.start);
  const [dateEnd, setDateEnd] = useState<string>(defaultRange.end);

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);

  const [zones, setZones] = useState<IZoneLookup[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);

  /** ---- applied: ตัวกรองที่กดค้นหาแล้ว — ตาราง/Excel อ่านจากตัวนี้เท่านั้น ---- */
  const [appliedParams, setAppliedParams] = useState<PaymentListReportParams>(() => ({
    ...(branchId ? { branchId } : {}),
    start_date: defaultRange.start,
    end_date: defaultRange.end,
  }));

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้สลับแท็บก่อน response กลับมา */
    let isMounted = true;

    /**
     * ใช้ GET zone/list ตามที่ระบุมา — คืน array ของโซนพร้อม `stall[]` ซ้อนมาในก้อนเดียว
     * จึงทำ dropdown โซน + แผงค้าแบบ cascading ได้จากการยิงครั้งเดียว
     * (หน้า announce ใช้เส้นนี้อยู่ ดู pages/announce/components/stallPicker.utils.ts)
     *
     * ไม่ส่ง `status` โดยตั้งใจ — รายงานอ้างใบแจ้งหนี้ย้อนหลัง ต้องเห็นทุกโซนที่อาจมีข้อมูล
     * ไม่ใช่เฉพาะโซนที่สถานะ CONTRACT อยู่ตอนนี้ · `skipcalculation: 0` = ไม่ต้องคำนวณ
     * ค่าน้ำค่าไฟแนบมาด้วย เร็วกว่ามากและ dropdown ไม่ได้ใช้
     */
    const loadZones = async () => {
      try {
        const res = await ZoneApi.list({
          page: 1,
          pageLimit: 1000,
          branchId,
          skipcalculation: 0,
        });
        return toZoneLookup(res?.data);
      } catch (error) {
        // คืน 404 ตอนไม่มีข้อมูล (ไม่ใช่ []) axios จึงโยน exception — ถือเป็น dropdown ว่าง
        return [];
      }
    };

    const loadPaymentTypes = async () => {
      try {
        // ต้องส่ง pageLimit ให้พอ default ของเส้นนี้คือ 10 จะได้ไม่ครบ
        const res = await PaymentTypeApi.findAll({ page: 1, pageLimit: 100, status: "ACTIVE" });
        const rows: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        return rows
          .map((row: any) => ({
            paymentServiceId: Number(row?.paymentServiceId),
            paymentName: row?.paymentName ?? null,
          }))
          .filter(
            (row: PaymentTypeOption) =>
              Number.isFinite(row.paymentServiceId) &&
              row.paymentServiceId !== FINE_PAYMENT_SERVICE_ID
          );
      } catch (error) {
        // 404 = ไม่มีข้อมูล ถือเป็น dropdown ว่าง ไม่ต้องเด้ง error (ตัวกรองไม่ใช่ข้อมูลหลัก)
        return [];
      }
    };

    const loadFilterMeta = async () => {
      // ยิงขนานได้ ไม่ต้องรอกัน · allSettled เพื่อให้ตัวที่พลาดไม่ล้มอีกตัว
      const [zoneResult, paymentTypeResult] = await Promise.allSettled([
        loadZones(),
        loadPaymentTypes(),
      ]);
      if (!isMounted) return;
      setZones(zoneResult.status === "fulfilled" ? zoneResult.value : []);
      setPaymentTypes(paymentTypeResult.status === "fulfilled" ? paymentTypeResult.value : []);
    };

    loadFilterMeta();
    return () => {
      isMounted = false;
    };
  }, [branchId]);

  /** ยังไม่เลือกโซน = รวมแผงของทุกโซน (เลือกแผงตรง ๆ ได้เลยไม่ต้องไล่หาโซนก่อน) */
  const stallOptions = useMemo<IStallLookup[]>(() => {
    if (zoneId === PAYMENT_LIST_FILTER_ALL) {
      return zones.reduce<IStallLookup[]>((all, zone) => all.concat(zone.stall || []), []);
    }
    return zones.find((zone) => zone.zoneId === zoneId)?.stall || [];
  }, [zones, zoneId]);

  /**
   * เปลี่ยนโซนต้องล้างแผงค้าเดิม ไม่งั้นค้างแผงของโซนอื่นแล้วส่ง zoneId + stallId ที่ขัดกัน
   * ตารางจะว่างแบบหาสาเหตุไม่เจอ
   *
   * handler ของ draft ทุกตัวตั้งใจ "ไม่" setPage(1) — ยังไม่ apply จะรีเซ็ตหน้าตอนนี้ไม่ได้
   * (ต่างจาก useStallReclaimFilter ที่ยิงทันทีจึงต้องรีเซ็ตทุกครั้ง) รีเซ็ตเกิดที่ applySearch
   */
  const handleChangeZone = (value: FilterValue) => {
    setZoneId(value);
    setStallId(PAYMENT_LIST_FILTER_ALL);
  };

  const handleChangeDateStart = (value: any) => {
    // InputDatePicker โชว์เป็น พ.ศ. แต่ค่าที่ส่ง backend ต้องเป็น ค.ศ. ISO
    setDateStart(value ? moment(value).format("YYYY-MM-DD") : "");
  };

  const handleChangeDateEnd = (value: any) => {
    setDateEnd(value ? moment(value).format("YYYY-MM-DD") : "");
  };

  const handleChangePageLimit = (value: number) => {
    setPageLimit(value);
    setPage(1);
  };

  /** ประกอบ query จากค่า draft ปัจจุบัน — ใส่เฉพาะ field ที่มีค่าจริง */
  const buildParams = useCallback((): PaymentListReportParams => {
    let condition: PaymentListReportParams = {};
    if (branchId) condition = { ...condition, branchId };
    if (keyword.trim()) condition = { ...condition, keyword: keyword.trim() };
    if (zoneId !== PAYMENT_LIST_FILTER_ALL) condition = { ...condition, zoneId: zoneId as number };
    if (stallId !== PAYMENT_LIST_FILTER_ALL) {
      condition = { ...condition, stallId: stallId as number };
    }
    if (paymentServiceId !== PAYMENT_LIST_FILTER_ALL) {
      condition = { ...condition, paymentServiceId: paymentServiceId as number };
    }
    if (dateStart) condition = { ...condition, start_date: dateStart };
    if (dateEnd) condition = { ...condition, end_date: dateEnd };
    return condition;
  }, [branchId, keyword, zoneId, stallId, paymentServiceId, dateStart, dateEnd]);

  /**
   * ปุ่มค้นหา — คืน object ใหม่เสมอ ทำให้กดซ้ำด้วยค่าเดิมก็ยิงใหม่ได้ ไม่ต้องมี counter แยก
   * setAppliedParams + setPage อยู่ใน handler เดียว React 18 batch ให้ ตารางจึงยิงรอบเดียว
   */
  const applySearch = useCallback(() => {
    setAppliedParams(buildParams());
    setPage(1);
  }, [buildParams]);

  /** ข้อความในลิสต์ตัวเลือกแผงค้า — ชื่อร้าน + เลขแผง ให้แยกแผงที่ชื่อซ้ำกันได้ */
  const stallOptionLabel = (stall: IStallLookup) => stallLabel(stall);

  /** ข้อความบนกล่อง dropdown ประเภท (ต้องคู่กับ displayEmpty ของ FilterSelect) */
  const paymentTypeLabel = useMemo(() => {
    if (paymentServiceId === PAYMENT_LIST_FILTER_ALL) return "ทั้งหมด";
    return (
      paymentTypes.find((type) => type.paymentServiceId === paymentServiceId)?.paymentName ||
      "ทั้งหมด"
    );
  }, [paymentTypes, paymentServiceId]);

  return {
    keyword,
    setKeyword,
    zoneId,
    handleChangeZone,
    stallId,
    setStallId,
    stallOptionLabel,
    paymentServiceId,
    setPaymentServiceId,
    paymentTypes,
    paymentTypeLabel,
    dateStart,
    handleChangeDateStart,
    dateEnd,
    handleChangeDateEnd,
    page,
    setPage,
    pageLimit,
    handleChangePageLimit,
    zones,
    /** ลิสต์ที่กรองตามโซนที่เลือกแล้ว — คอมโพเนนต์ใช้ตัวนี้ทำ option */
    stallOptions,
    /** ตัวกรองที่กดค้นหาแล้ว — ใช้ทั้งตารางและปุ่ม Excel */
    appliedParams,
    applySearch,
  };
};
